import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import { useState, useEffect, useRef, useCallback, useId } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { InfoRegular, CheckmarkFilled, DismissRegular } from "@fluentui/react-icons";
import {
  FluentProvider,
  webLightTheme,
  webDarkTheme,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  MenuSplitGroup,
  MenuDivider,
  Button,
} from "@fluentui/react-components";
import { ArrowDownloadRegular, ChevronDownRegular } from "@fluentui/react-icons";
import { authService } from "@/services/authService";
import { directoryService } from "@/services/directoryService";
import { apiService } from "@/services/apiService";
import { useTranslations } from "@/hooks/useTranslations";
import { useApplicationInsights } from "@/hooks/useApplicationInsights";
import type { IDirectory } from "@/types/directoryTypes";
import { useCurrentPage } from "@/hooks/useCurrentPage";
import { getLocalePreference } from "@/utils/localeStorageHelpers";
import styles from "./TopBar.module.scss";

interface ITopBarProps {
  onSignIn?: () => void;
}

/**
 * TopBar component - displays below UHF header with profile panel and directory switching
 * Design based on Partner Center directory switching UI
 */
export function TopBar({ onSignIn }: ITopBarProps): ReactNode {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [prefersDarkMode, setPrefersDarkMode] = useState(
    typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
  );
  const [userName, setUserName] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string>("");
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const [currentTenantId, setCurrentTenantId] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [directories, setDirectories] = useState<IDirectory[]>([]);
  const [isLoadingDirectories, setIsLoadingDirectories] = useState(false);
  const [directoryError, setDirectoryError] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const directoryTooltipId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const params = useParams();
  const { t } = useTranslations();
  const { trackEvent, trackException } = useApplicationInsights();
  const { pageId, isHomePage, isNavLoaded, isMobileNavOpen, setIsMobileNavOpen } = useCurrentPage();

  // Prefer URL locale, fall back to stored preference
  const locale = (params?.locale as string) || getLocalePreference()?.localeCode;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const update = (): void => setPrefersDarkMode(mediaQuery.matches);
    update();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", update);
      return (): void => mediaQuery.removeEventListener("change", update);
    }

    // Safari < 14
    if (mediaQuery.addListener) {
      mediaQuery.addListener(update);
      return (): void => {
        mediaQuery.removeListener(update);
      };
    }

    return;
  }, []);

  // Close panel on Escape key and trap focus within the panel
  const handlePanelKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>): void => {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
        triggerButtonRef.current?.focus();
        return;
      }

      if (event.key === "Tab" && panelRef.current) {
        const focusableElements = panelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
        );

        if (focusableElements.length === 0) {
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const target = event.target as HTMLElement;

        if (event.shiftKey) {
          if (target === firstElement || target === panelRef.current) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (target === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    },
    []
  );

  useEffect(() => {
    async function initAuth(): Promise<void> {
      try {
        await authService.createAuthProvider();

        const account = authService.getCurrentAccount();
        if (account) {
          setIsAuthenticated(true);
          setUserName(account.name || account.username);
          // Use the tenant-specific UPN from ID token claims when available (e.g., after tenant switch),
          // otherwise fall back to account.username (home tenant UPN).
          const claims = account.idTokenClaims as Record<string, unknown> | undefined;
          const tenantEmail = (claims?.preferred_username as string)
            ?? (claims?.upn as string)
            ?? account.username;
          setUserEmail(tenantEmail);
          setCurrentTenantId(account.tenantId || "");
          // Fetch profile photo
          void fetchProfilePhoto();
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      }
    }

    async function fetchProfilePhoto(): Promise<void> {
      const photoUrl = await authService.getProfilePhoto();
      setUserPhoto(photoUrl);
    }

    void initAuth();
  }, []);

  // Load directories when dropdown opens
  useEffect(() => {
    async function loadDirectories(): Promise<void> {
      if (isDropdownOpen && isAuthenticated && directories.length === 0 && !directoryError) {
        setIsLoadingDirectories(true);
        setDirectoryError(false);
        try {
          const dirs = await directoryService.getDirectories();
          setDirectories(dirs);
        } catch (error) {
          console.error("Error loading directories:", error);
          setDirectoryError(true);
        } finally {
          setIsLoadingDirectories(false);
        }
      }
    }

    void loadDirectories();
  }, [isDropdownOpen, isAuthenticated, directories.length, directoryError]);

  useEffect((): (() => void) => {
    function handleClickOutside(event: MouseEvent): void {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        triggerButtonRef.current &&
        !triggerButtonRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Prevent body scroll when panel is open
      document.body.style.overflow = "hidden";
      const root = document.getElementById("root");
      if (root) {
        root.setAttribute("inert", "");
      }
    }

    return (): void => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "";

      const root = document.getElementById("root");
      if (root) {
        root.removeAttribute("inert");
      }
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen) {
      panelRef.current?.focus();
    } else {
      triggerButtonRef.current?.focus();
    }
  }, [isDropdownOpen]);

  const handleSignIn = async (): Promise<void> => {
    if (onSignIn) {
      onSignIn();
      return;
    }

    try {
      await authService.login();
    } catch (error) {
      console.error("Sign in failed:", error);
    }
  };

  const handleSignOut = async (): Promise<void> => {
    try {
      setIsDropdownOpen(false);
      await authService.clearCacheAndLogout();
    } catch (error) {
      console.error("Sign out failed:", error);
    }
  };

  const handleSignInWithAnotherAccount = async (): Promise<void> => {
    try {
      setIsDropdownOpen(false);
      await authService.switchAccount();
    } catch (error) {
      console.error("Sign in with another account failed:", error);
    }
  };

  const handleDirectorySwitch = async (tenantId: string): Promise<void> => {
    if (tenantId === currentTenantId) {
      return;
    }

    try {
      setIsDropdownOpen(false);
      await authService.switchTenant(tenantId);
    } catch (error) {
      console.error("Directory switch failed:", error);
    }
  };

  const toggleDropdown = (): void => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleDownload = useCallback(
    async (isFullGuide: boolean): Promise<void> => {
      // For full guide, pageId is optional; for single page download, pageId is required
      if ((!isFullGuide && !pageId) || isDownloading) {
        return;
      }

      try {
        setIsDownloading(true);

        trackEvent(isFullGuide ? "downloadFullGuideStarted" : "downloadStarted", {
          contentId: pageId || "",
          locale: locale || "default",
          isHomePage: isHomePage.toString()
        });

        // For full guide, pass empty string as pageId (API ignores it for full guide)
        const contentId = pageId || "";

        const { blob, filename } = locale
          ? await apiService.exportContent(contentId, isFullGuide, locale)
          : await apiService.exportContent(contentId, isFullGuide);

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        trackEvent(isFullGuide ? "downloadFullGuideSuccess" : "downloadSuccess", {
          filename,
          contentId,
          locale: locale || "default",
          isHomePage: isHomePage.toString(),
          fileSize: blob.size.toString()
        });
      } catch (error) {
        console.error("Download failed:", error);
        
        trackException(
          error instanceof Error ? error : new Error("Download failed"),
          undefined,
          {
            contentId: pageId || "",
            locale: locale || "default", 
            isHomePage: isHomePage.toString(),
            downloadType: isFullGuide ? "fullGuide" : "singlePage"
          }
        );
        
        trackEvent(isFullGuide ? "downloadFullGuideFailed" : "downloadFailed", {
          error: error instanceof Error ? error.message : "Unknown error",
          contentId: pageId || "",
          locale: locale || "default",
          isHomePage: isHomePage.toString()
        });
      } finally {
        setIsDownloading(false);
      }
    },
    [pageId, isDownloading, locale, trackEvent, trackException, isHomePage]
  );

  const getInitials = (name: string): string => {
    if (!name) {
      return "?";
    }
    const parts = name.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        {/* Opens the Table of contents (LeftNav) */}
        <button
          type="button"
          className={styles.mobileMenuButton}
          onClick={(): void => setIsMobileNavOpen(!isMobileNavOpen)}
          aria-label={t("navigation.tableOfContents")}
          aria-expanded={isMobileNavOpen}
        >
          <span className={styles.mobileMenuLabel}>{t("navigation.tableOfContents")}</span>
          <ChevronDownRegular className={styles.mobileMenuCaret} />
        </button>

        {/* Download Full Guide Button - show on home page after navigation is loaded */}
        {isHomePage && isNavLoaded && (
          <div className={styles.downloadWrapper}>
            <FluentProvider theme={prefersDarkMode ? webDarkTheme : webLightTheme}>
              <Button
                appearance="outline"
                icon={<ArrowDownloadRegular />}
                onClick={(): void => void handleDownload(true)}
                disabled={isDownloading}
              >
                <span className={styles.downloadButtonLabel}>
                  {isDownloading ? t("download.downloading") : t("download.downloadFullGuide")}
                </span>
              </Button>
            </FluentProvider>
          </div>
        )}

        {/* Download Split Button - show on content pages when pageId is available */}
        {!isHomePage && pageId && (
          <div className={styles.downloadWrapper}>
            <FluentProvider theme={prefersDarkMode ? webDarkTheme : webLightTheme}>
              <MenuSplitGroup>
                <Button
                  appearance="outline"
                  icon={<ArrowDownloadRegular />}
                  onClick={(): void => void handleDownload(false)}
                  disabled={isDownloading}
                >
                  <span className={styles.downloadButtonLabel}>
                    {isDownloading ? t("download.downloading") : t("download.download")}
                  </span>
                </Button>
                <Menu positioning="below-end">
                  <MenuTrigger disableButtonEnhancement>
                    <Button
                      appearance="outline"
                      icon={<ChevronDownRegular />}
                      disabled={isDownloading}
                      aria-label="Download options"
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem onClick={(): void => void handleDownload(false)}>
                        {t("download.download")}
                      </MenuItem>
                      <MenuDivider />
                      <MenuItem onClick={(): void => void handleDownload(true)}>
                        {t("download.downloadFullGuide")}
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </MenuSplitGroup>
            </FluentProvider>
          </div>
        )}

        {isAuthenticated ? (
          <div className={styles.userMenu}>
            <button
              ref={triggerButtonRef}
              onClick={toggleDropdown}
              className={styles.signInButton}
              aria-label={t("auth.signedInAs", { userName })}
              aria-expanded={isDropdownOpen}
              aria-haspopup="dialog"
            >
              {userPhoto ? (
                <img src={userPhoto} alt="" className={styles.headerAvatar} aria-hidden="true" />
              ) : (
                <div className={styles.headerAvatarInitials} aria-hidden="true">
                  {getInitials(userName)}
                </div>
              )}
              <span className={styles.signInText}>{userName}</span>
            </button>

            {isDropdownOpen &&
              createPortal(
                <>
                  <div className={styles.overlay} aria-hidden="true" />
                  {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog needs keydown for focus trap */}
                  <div
                    className={styles.sidePanel}
                    role="dialog"
                    aria-label={t("auth.accountPanel")}
                    aria-modal="true"
                    tabIndex={-1}
                    ref={panelRef}
                    onKeyDown={handlePanelKeyDown}
                  >
                    <div className={styles.panelHeader}>
                      <button
                        type="button"
                        className={styles.closeButton}
                        onClick={(): void => setIsDropdownOpen(false)}
                        aria-label={t("common.close")}
                      >
                        <DismissRegular />
                      </button>
                    </div>

                    {/* Profile Section */}
                    <div className={styles.profileSection}>
                      <div className={styles.profileInfo}>
                        {userPhoto ? (
                          <img
                            src={userPhoto}
                            alt=""
                            className={styles.profileAvatar}
                            aria-hidden="true"
                          />
                        ) : (
                          <div className={styles.profileAvatar} aria-hidden="true">
                            {getInitials(userName)}
                          </div>
                        )}
                        <div className={styles.profileDetails}>
                          <div className={styles.profileName}>{userName}</div>
                          <div className={styles.profileEmail}>{userEmail}</div>
                        </div>
                      </div>
                    </div>

                    {/* Directories Section */}
                    <div className={styles.directoriesSection}>
                      <div className={styles.directoriesHeader}>
                        <span>{t("directory.azureAdDirectories")}</span>
                        <button
                          className={styles.infoIcon}
                          aria-label={t("directory.infoTooltip")}
                          aria-describedby={showTooltip ? directoryTooltipId : undefined}
                          onMouseEnter={(): void => setShowTooltip(true)}
                          onMouseLeave={(): void => setShowTooltip(false)}
                          onFocus={(): void => setShowTooltip(true)}
                          onBlur={(): void => setShowTooltip(false)}
                        >
                          <InfoRegular />
                        </button>
                        {showTooltip && (
                          <div id={directoryTooltipId} className={styles.tooltip} role="tooltip">
                            {t("directory.infoTooltip")}
                          </div>
                        )}
                      </div>

                      {isLoadingDirectories ? (
                        <div className={styles.loadingDirectories}>{t("directory.loading")}</div>
                      ) : directoryError ? (
                        <div className={styles.loadingDirectories}>{t("directory.errorLoading")}</div>
                      ) : (
                        <div
                          className={styles.directoryList}
                          role="listbox"
                          aria-label={t("directory.azureAdDirectories")}
                        >
                          {directories.map((directory) => (
                            <button
                              key={directory.tenantId}
                              type="button"
                              className={`${styles.directoryItem} ${directory.tenantId === currentTenantId ? styles.selected : ""}`}
                              role="option"
                              aria-selected={directory.tenantId === currentTenantId}
                              onClick={(): void => void handleDirectorySwitch(directory.tenantId)}
                            >
                              <div className={styles.directoryCheck}>
                                {directory.tenantId === currentTenantId && (
                                  <CheckmarkFilled
                                    className={styles.checkIcon}
                                    aria-hidden="true"
                                  />
                                )}
                              </div>
                              <div className={styles.directoryName}>{directory.displayName}</div>
                            </button>
                          ))}
                        </div>
                      )}

                    </div>

                    {/* Actions Section - below directory list */}
                    <div className={styles.actionsSection}>
                      <button
                        className={styles.signInLink}
                        onClick={(): void => void handleSignInWithAnotherAccount()}
                      >
                        {t("directory.signInWithAnother")}
                      </button>
                      <button
                        className={styles.signOutBtn}
                        onClick={(): void => void handleSignOut()}
                      >
                        {t("auth.signOut")}
                      </button>
                    </div>
                  </div>
                </>,
                document.body
              )}
          </div>
        ) : (
          <button
            onClick={handleSignIn}
            className={styles.signInButton}
            aria-label={t("auth.signIn")}
          >
            <span className={styles.signInIcon} aria-hidden="true">
              &#xE77B;
            </span>
            <span className={styles.signInText}>{t("auth.signIn")}</span>
          </button>
        )}
      </div>
    </div>
  );
}
